import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { clearPersistedFilters } from "@/lib/persistedFilters";
import { toast } from "sonner";

const AuthContext = createContext(null);
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutos (RD-Ley LOPD/GDPR — seguridad de sesión)
const WARN_BEFORE_MS = 60 * 1000;         // Avisar 1 min antes

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [error, setError] = useState("");
    const idleTimerRef = useRef(null);
    const warnTimerRef = useRef(null);

    const bootstrap = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            if (e?.response?.status !== 401) console.error("Auth bootstrap error", e);
            setUser(false);
        }
    }, []);

    useEffect(() => { bootstrap(); }, [bootstrap]);

    const login = useCallback(async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data.user);
            return true;
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            setError(msg);
            return false;
        }
    }, []);

    const logout = useCallback(async (reason) => {
        try { await api.post("/auth/logout"); } catch (e) { console.error("Logout error", e); }
        if (reason === "idle") toast.warning("Sesión cerrada por inactividad (30 min)");
        clearPersistedFilters();
        setUser(false);
    }, []);

    // Auto-logout tras 30 min de inactividad (protección datos LOPD)
    useEffect(() => {
        if (!user || user === false) return;
        const reset = () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
            warnTimerRef.current = setTimeout(() => {
                toast.info("Tu sesión se cerrará en 1 minuto por inactividad. Mueve el ratón para continuar.");
            }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
            idleTimerRef.current = setTimeout(() => { logout("idle"); }, IDLE_TIMEOUT_MS);
        };
        const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            events.forEach((e) => window.removeEventListener(e, reset));
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
        };
    }, [user, logout]);

    const value = useMemo(
        () => ({ user, setUser, login, logout, error, reloadMe: bootstrap }),
        [user, login, logout, error, bootstrap]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);

